import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useEffect, useRef, useState } from 'react';

const VIEWER_PLUGIN_CODE = 'PERSPECTIVE_TRX_LOOKUP_VIEWER_PLUGIN';

// TODO: When count is loading, then '-'
export const PerspectiveTrxLookupViewerGrid = ({
  viewerReqParam,
  entity,
  onOppositeSide,
  side,
  oppositeExpr,
  onExprChange,
  onExportReqChange,
}) => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  const gridRef = useRef();

  const [fieldDefs, setFieldDefs] = useState();

  useEffect(() => {
    const reqParam = {
      ...viewerReqParam,
      entity,
      viewerPluginCode: VIEWER_PLUGIN_CODE,
      operation: 'FIELD_DEFS',
    };
    rsrc.data(module, 'VIEWER_DATA_PLUGIN', reqParam, entity).then((fieldDefs) => {
      if (fieldDefs.length === 0) {
        setFieldDefs([]);
        return;
      }

      // The relation appends the derived flag last; the grid (and the export, which follows the
      // fieldDefs order) shows it second, right after the key.
      const onOppositeSideIndex = fieldDefs.findIndex((fieldDef) => fieldDef.field === 'onOppositeSide');
      const [onOppositeSideFieldDef] = fieldDefs.splice(onOppositeSideIndex, 1);
      // The mode buttons filter this derived flag out-of-band, so it carries no direct column filter.
      onOppositeSideFieldDef.filter = false;
      fieldDefs.splice(1, 0, onOppositeSideFieldDef);
      setFieldDefs(fieldDefs);
    });
  }, []);

  // The mode and opposite filter are captured in the datasource's getRows closure, so when they
  // change the datasource must be re-installed (which also triggers a refetch). Skipped until AG
  // Grid is ready; the grid's own onGridReady event installs the initial datasource.
  useEffect(() => {
    if (!gridRef.current?.gridApiRef?.current) return;
    setViewerDataSource();
  }, [onOppositeSide, oppositeExpr]);

  const setViewerDataSource = () => {
    const dataSource = {
      getRows: (params) => {
        const expr = ExprUtils.filterModelToExpr(params.filterModel, fieldDefs);

        const reqParam = {
          ...viewerReqParam,
          entity,
          viewerPluginCode: VIEWER_PLUGIN_CODE,
          operation: 'ROWS',
          // '' (no button selected) omits the filter so both matched and unmatched rows show.
          onOppositeSide: onOppositeSide || undefined,
          // The opposite grid's filter narrows which opposite rows count toward this side's flag.
          oppositeExpr,
          expr,
          rowsReq: {
            startRow: params.startRow,
            endRow: params.endRow,
            expr,
            sortModel: params.sortModel,
          },
        };

        if (onExportReqChange) {
          const sideUpdate =
            side === 'left' ?
              { leftViewerReqParam: reqParam, leftFieldDefs: fieldDefs }
            : { rightViewerReqParam: reqParam, rightFieldDefs: fieldDefs };
          onExportReqChange(sideUpdate);
        }

        rsrc.data(module, 'VIEWER_DATA_PLUGIN', reqParam, entity).then((res) => {
          if (gridRef.current) {
            gridRef.current.onGridSuccess(res, params);
            gridRef.current.resize();
          }
        });

        // Count and totals depend only on the filter, not the scrolled page, so fetch them once
        // per filter (first block) instead of on every infinite-scroll block.
        if (params.startRow === 0) {
          const summaryReqParam = {
            ...viewerReqParam,
            entity,
            viewerPluginCode: VIEWER_PLUGIN_CODE,
            operation: 'AGGREGATES',
            onOppositeSide: onOppositeSide || undefined,
            oppositeExpr,
            rowsReq: { expr },
          };
          rsrc.data(module, 'VIEWER_DATA_PLUGIN', summaryReqParam, entity).then((summary) => {
            if (gridRef.current) {
              gridRef.current.setRowCount(summary.count);
              gridRef.current.setTotalsRow(summary.totals);
            }
          });
        }
      },
    };

    gridRef.current.setDataSource(dataSource);
  };

  return (
    fieldDefs && (
      <Grid
        ref={gridRef}
        gridOptions={{ fieldDefs }}
        events={{
          onGridReady: setViewerDataSource,
          onFilterChanged: (event) => {
            onExprChange(ExprUtils.filterModelToExpr(event.api.getFilterModel(), fieldDefs));
          },
        }}
      />
    )
  );
};
